// scanner-fp: `useCallback` deps intentionally omit `api` (module singleton), setters (stable), and local vars. See /app/SECURITY_SCANNER_IGNORE.md §4.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, ArrowRight, Trash2, Mail, Phone, Building2, Clock, AlertTriangle, Sparkles, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import MasterSelect from "@/components/MasterSelect";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const STAGES = [
  { key: "new", label: "New", color: "bg-slate-500" },
  { key: "contacted", label: "Contacted", color: "bg-blue-500" },
  { key: "qualified", label: "Qualified", color: "bg-amber-500" },
  { key: "proposal", label: "Proposal", color: "bg-purple-500" },
  { key: "won", label: "Won", color: "bg-emerald-600" },
  { key: "lost", label: "Lost", color: "bg-rose-500" },
];

const QUALIFICATIONS = [
  { key: "SQL", label: "SQL", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
  { key: "MQL", label: "MQL", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
  { key: "unqualified", label: "Unqualified", cls: "bg-slate-500/15 text-slate-600 border-slate-500/30" },
  { key: "disqualified", label: "Disqualified", cls: "bg-rose-500/15 text-rose-600 border-rose-500/30" },
];

function qualStyle(q) {
  return QUALIFICATIONS.find(x => x.key === q)?.cls || QUALIFICATIONS[2].cls;
}

// Countdown / breach indicator — pure function of due timestamp
function slaState(lead) {
  if (lead?.first_contact_at) return { done: true, label: "Contacted", cls: "text-emerald-600" };
  const due = lead?.first_contact_due_at;
  if (!due) return null;
  const ms = new Date(due).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) {
    const overH = Math.round(-ms / 3_600_000);
    return { breached: true, label: `Breached${overH ? ` · ${overH}h over` : ""}`, cls: "text-rose-600" };
  }
  const hrs = Math.floor(ms / 3_600_000);
  const mins = Math.floor((ms % 3_600_000) / 60_000);
  const label = hrs > 0 ? `${hrs}h ${mins}m left` : `${mins}m left`;
  const cls = ms < 2 * 3_600_000 ? "text-amber-600" : "text-muted-foreground";
  return { label, cls };
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [teammates, setTeammates] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", source: "", stage: "new", value_gbp: 0, notes: "" });
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteBody, setNoteBody] = useState("");
  const [filterQual, setFilterQual] = useState("all");
  const [onlyBreached, setOnlyBreached] = useState(false);
  // Tick every 30s so SLA countdowns stay fresh on-screen
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    const params = {};
    if (filterQual !== "all") params.qualification = filterQual;
    if (onlyBreached) params.breached = true;
    const r = await api.get("/crm/leads", { params });
    setLeads(r.data);
  }, [filterQual, onlyBreached]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/crm/teammates").then(r => setTeammates(r.data)).catch(() => {});
  }, []);

  const teamById = useMemo(() => Object.fromEntries(teammates.map(t => [t.id, t])), [teammates]);

  const loadNotes = async (leadId) => {
    const r = await api.get("/crm/notes", { params: { related_type: "lead", related_id: leadId } });
    setNotes(r.data);
  };

  const refreshSelected = async (id) => {
    const r = await api.get(`/crm/leads/${id}`);
    setSelected(r.data);
  };

  const handleSave = async () => {
    try {
      await api.post("/crm/leads", { ...form, value_gbp: parseFloat(form.value_gbp || 0) });
      toast.success("Lead created");
      setDialogOpen(false);
      setForm({ name: "", email: "", phone: "", company: "", source: "", stage: "new", value_gbp: 0, notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const handleStage = async (lead, stage) => {
    await api.post(`/crm/leads/${lead.id}/stage`, { stage });
    load();
    if (selected?.id === lead.id) refreshSelected(lead.id);
  };

  const handleQualify = async (lead, qualification) => {
    await api.post(`/crm/leads/${lead.id}/qualify`, { qualification });
    toast.success(`Marked as ${qualification}`);
    load();
    if (selected?.id === lead.id) refreshSelected(lead.id);
  };

  const handleConvert = async (lead) => {
    await api.post(`/crm/leads/${lead.id}/convert`);
    toast.success("Lead converted to a Deal");
    load();
    setSelected(null);
  };

  const handleDelete = async (lead) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/crm/leads/${lead.id}`);
    toast.success("Deleted");
    setSelected(null);
    load();
  };

  const addNote = async () => {
    if (!noteBody.trim()) return;
    await api.post("/crm/notes", { body: noteBody, related_type: "lead", related_id: selected.id });
    setNoteBody("");
    loadNotes(selected.id);
    refreshSelected(selected.id);
  };

  const assigneeName = (uid) => uid ? (teamById[uid]?.name || teamById[uid]?.email || uid.slice(0, 6)) : "Unassigned";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-semibold">Leads pipeline</h2>
        <div className="flex items-center gap-2">
          <Select value={filterQual} onValueChange={setFilterQual}>
            <SelectTrigger className="w-40" data-testid="lead-filter-qual"><SelectValue placeholder="Qualification" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All qualifications</SelectItem>
              {QUALIFICATIONS.map(q => <SelectItem key={q.key} value={q.key}>{q.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant={onlyBreached ? "destructive" : "outline"}
            size="sm"
            onClick={() => setOnlyBreached(v => !v)}
            data-testid="lead-filter-breached"
          >
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
            {onlyBreached ? "Showing breached only" : "SLA breached"}
          </Button>
          <BulkInviteReferrerButton leads={leads} onDone={load} />
          <Button onClick={() => setDialogOpen(true)} data-testid="crm-new-lead-btn">
            <Plus className="w-4 h-4 mr-2" /> New lead
          </Button>
        </div>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {STAGES.map((s) => {
          const items = leads.filter(l => l.stage === s.key);
          return (
            <div key={s.key} className="rounded-xl border border-border bg-card p-3 min-h-[220px]" data-testid={`lead-col-${s.key}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${s.color}`} />
                  <span className="text-sm font-semibold">{s.label}</span>
                </div>
                <Badge variant="secondary">{items.length}</Badge>
              </div>
              <div className="space-y-2">
                {items.map((l) => {
                  const sla = slaState(l);
                  return (
                    <button
                      key={l.id}
                      onClick={() => { setSelected(l); loadNotes(l.id); }}
                      data-testid={`lead-card-${l.id}`}
                      className="w-full text-left rounded-lg bg-muted/50 hover:bg-muted transition-colors p-3 border border-border/50"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">{l.name}</div>
                          <div className="text-xs text-muted-foreground truncate">{l.company || l.email || "—"}</div>
                        </div>
                        <span
                          className={`shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded border ${qualStyle(l.qualification)}`}
                          data-testid={`lead-qual-${l.id}`}
                        >
                          {l.qualification || "unqualified"}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[11px]">
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <Sparkles className="w-3 h-3" /> {l.total_score ?? 0}/100
                        </span>
                        {l.value_gbp ? (
                          <span className="font-semibold text-primary">£{Number(l.value_gbp).toLocaleString()}</span>
                        ) : null}
                      </div>
                      {sla && (
                        <div className={`mt-1.5 inline-flex items-center gap-1 text-[11px] ${sla.cls}`} data-testid={`lead-sla-${l.id}`}>
                          {sla.breached ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                          {sla.label}
                        </div>
                      )}
                    </button>
                  );
                })}
                {items.length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-4">No leads</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New lead</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label className="text-xs">Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="lead-form-name" />
            </div>
            <div><Label className="text-xs">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="lead-form-email" />
            </div>
            <div><Label className="text-xs">Phone</Label>
              <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
            </div>
            <div><Label className="text-xs">Company</Label>
              <Input value={form.company} onChange={e => setForm({...form, company: e.target.value})} />
            </div>
            <div><Label className="text-xs">Source</Label>
              <MasterSelect
                entityType="lead_source"
                value={form.source}
                onChange={(v) => setForm({ ...form, source: v })}
                placeholder="Choose source"
                testId="lead-source"
              />
            </div>
            <div><Label className="text-xs">Stage</Label>
              <Select value={form.stage} onValueChange={v => setForm({...form, stage: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Value £</Label>
              <Input type="number" value={form.value_gbp} onChange={e => setForm({...form, value_gbp: e.target.value})} />
            </div>
            <div className="col-span-2"><Label className="text-xs">Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="lead-form-save">Create lead</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl">
          {selected && (() => {
            const sla = slaState(selected);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center justify-between gap-2 flex-wrap">
                    <span>{selected.name}</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${qualStyle(selected.qualification)}`}>
                        {selected.qualification || "unqualified"}
                      </span>
                      <Badge>{STAGES.find(s => s.key === selected.stage)?.label}</Badge>
                    </div>
                  </DialogTitle>
                </DialogHeader>

                {/* Scoring + SLA */}
                <div className="grid grid-cols-3 gap-2 rounded-lg bg-muted/50 p-3 text-xs">
                  <div>
                    <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Fit</div>
                    <div className="font-semibold text-base" data-testid="lead-detail-fit">{selected.fit_score ?? 0}<span className="text-muted-foreground text-xs">/50</span></div>
                  </div>
                  <div>
                    <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Intent</div>
                    <div className="font-semibold text-base" data-testid="lead-detail-intent">{selected.intent_score ?? 0}<span className="text-muted-foreground text-xs">/50</span></div>
                  </div>
                  <div>
                    <div className="text-muted-foreground uppercase tracking-wider text-[10px]">Total</div>
                    <div className="font-semibold text-base" data-testid="lead-detail-total">{selected.total_score ?? 0}<span className="text-muted-foreground text-xs">/100</span></div>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-xs">
                  <div className="text-muted-foreground">
                    Assignee: <span className="font-medium text-foreground">{assigneeName(selected.assigned_to)}</span>
                  </div>
                  {sla && (
                    <div className={`inline-flex items-center gap-1 ${sla.cls}`} data-testid="lead-detail-sla">
                      {sla.breached ? <AlertTriangle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                      First-contact SLA: {sla.label}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selected.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-muted-foreground" />{selected.email}</div>}
                  {selected.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-muted-foreground" />{selected.phone}</div>}
                  {selected.company && <div className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5 text-muted-foreground" />{selected.company}</div>}
                  {selected.value_gbp ? <div className="font-semibold text-primary">£{Number(selected.value_gbp).toLocaleString()}</div> : null}
                </div>

                {/* Manual qualify */}
                <div className="flex gap-2 flex-wrap pt-2 border-t">
                  <span className="text-xs text-muted-foreground self-center mr-1">Qualify:</span>
                  {QUALIFICATIONS.map(q => (
                    <Button
                      key={q.key}
                      size="sm"
                      variant={selected.qualification === q.key ? "default" : "outline"}
                      onClick={() => handleQualify(selected, q.key)}
                      data-testid={`lead-qual-btn-${q.key}`}
                    >
                      {q.label}
                    </Button>
                  ))}
                </div>

                <div className="flex gap-2 flex-wrap pt-2 border-t">
                  <span className="text-xs text-muted-foreground self-center mr-1">Move to:</span>
                  {STAGES.map(s => (
                    <Button key={s.key} size="sm" variant={selected.stage === s.key ? "default" : "outline"}
                      onClick={() => handleStage(selected, s.key)}
                      data-testid={`lead-stage-${s.key}`}>
                      {s.label}
                    </Button>
                  ))}
                </div>

                {/* Notes */}
                <div className="border-t pt-3 space-y-2">
                  <div className="font-medium text-sm">Internal notes</div>
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {notes.map(n => (
                      <div key={n.id} className="rounded-lg bg-muted p-2 text-sm">
                        <div className="text-xs text-muted-foreground">{n.author_name} · {new Date(n.created_at).toLocaleString()}</div>
                        <div>{n.body}</div>
                      </div>
                    ))}
                    {notes.length === 0 && <div className="text-xs text-muted-foreground">No notes yet.</div>}
                  </div>
                  <div className="flex gap-2">
                    <Input placeholder="Write a note…" value={noteBody} onChange={e => setNoteBody(e.target.value)} data-testid="lead-note-input" />
                    <Button size="sm" onClick={addNote} data-testid="lead-note-save">Add</Button>
                  </div>
                </div>
                <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:justify-between">
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(selected)}>
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setSelected(null)}>Close</Button>
                    <Button onClick={() => handleConvert(selected)} data-testid="lead-convert-btn">
                      <ArrowRight className="w-4 h-4 mr-2" /> Convert to Deal
                    </Button>
                  </div>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
//   Bulk invite selected leads as referrers
// ---------------------------------------------------------------------------
function BulkInviteReferrerButton({ leads, onDone }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(() => new Set());
  const [types, setTypes] = useState([]);
  const [structures, setStructures] = useState([]);
  const [form, setForm] = useState({ type_code: "agent", commission_structure_id: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPicked(new Set());
    api.get("/masters/lookup/referrer_type").then((r) => setTypes(r.data || []));
    api.get("/masters/lookup/commission_structure").then((r) => setStructures(r.data || []));
  }, [open]);

  const filteredStructures = useMemo(() => {
    return structures.filter((s) => (s.data?.referrer_type_code || "") === form.type_code);
  }, [structures, form.type_code]);

  useEffect(() => {
    if (filteredStructures.length && !filteredStructures.find((s) => s.id === form.commission_structure_id)) {
      setForm((f) => ({ ...f, commission_structure_id: filteredStructures[0].id }));
    }
  }, [filteredStructures, form.commission_structure_id]);

  const eligible = leads.filter((l) => l.email);   // Must have an email
  const toggle = (id) => setPicked((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectAll = () => setPicked(new Set(eligible.map((l) => l.id)));

  const submit = async () => {
    if (!picked.size || !form.commission_structure_id) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/leads/bulk-invite-referrer", {
        lead_ids: Array.from(picked),
        type_code: form.type_code,
        commission_structure_id: form.commission_structure_id,
      });
      toast.success(`${data.created} invited · ${data.skipped} skipped`);
      setOpen(false); onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="leads-bulk-invite-btn">
        <Coins className="w-3.5 h-3.5 mr-1.5" /> Invite as referrers
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light">Bulk invite as referrers</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.type_code} onValueChange={(v) => setForm({ ...form, type_code: v, commission_structure_id: "" })}>
                  <SelectTrigger className="mt-1.5" data-testid="bulk-invite-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => <SelectItem key={t.id} value={t.data?.code || t.label.toLowerCase()}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Commission plan</Label>
                <Select value={form.commission_structure_id} onValueChange={(v) => setForm({ ...form, commission_structure_id: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="bulk-invite-structure">
                    <SelectValue placeholder={filteredStructures.length ? "Choose" : "No plans"} />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredStructures.map((s) => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs">
              <span>{picked.size} of {eligible.length} eligible leads selected</span>
              <Button variant="ghost" size="sm" onClick={selectAll} data-testid="bulk-invite-select-all">Select all eligible</Button>
            </div>
            <div className="border border-border rounded-md max-h-[300px] overflow-y-auto">
              {eligible.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No leads with email addresses.</div>}
              {eligible.map((l) => (
                <label key={l.id} className="flex items-center gap-3 px-3 py-2 border-b border-border last:border-0 text-sm hover:bg-muted/30">
                  <input type="checkbox" checked={picked.has(l.id)} onChange={() => toggle(l.id)} data-testid={`bulk-invite-lead-${l.id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{l.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{l.email}{l.company ? ` · ${l.company}` : ""}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={busy || !picked.size || !form.commission_structure_id} data-testid="bulk-invite-submit">
              {busy ? "Inviting…" : `Invite ${picked.size} lead${picked.size === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
